import { useQuery } from "@tanstack/vue-query";
import { HttpError, request } from "@/shared/http/client";

/**
 * QUEM ESTA SESSÃO É PARA A INSTALAÇÃO — e o que cada modo mostra.
 *
 * ── POR QUE ISTO MORA EM `shared/` ────────────────────────────────────────
 *
 * A resposta é lida em dois sítios que não se podem importar: `features/platform`
 * (o menu e o guard das rotas de administração) e `features/agents` (o painel do
 * Ben, que precisa de saber se desenha a linha de tokens). Uma feature nunca
 * importa outra, e duplicar a consulta seriam duas chaves de cache para o mesmo
 * `/v3/platform/access` — que é como uma tela fica um minuto atrás da outra sobre
 * quem a pessoa é.
 *
 * Uma ÚNICA `queryKey`, portanto, e um `select` por leitor.
 */

/**
 * Os eixos, exactamente como o servidor os nomeia
 * (`v3benvox_back/src/shared/config/agent-display.ts`).
 *
 * Repetidos aqui e não importados: os dois repositórios são dois deploys, e o
 * front tem de saber desenhar contra um servidor mais antigo que ainda não
 * conheça um eixo novo. O que os mantém honestos é o `DEFAULT_*` abaixo, que diz
 * a mesma coisa que o default do servidor — e o teste que os compara seria a
 * terceira cópia.
 */
export const AGENT_DISPLAY_AXES = [
	"activity",
	"reasoning",
	"reasoningRaw",
	"tools",
	"toolIo",
	"toolRaw",
	/**
	 * O PROMPT do turno, dentro do painel — o que o modelo recebeu, tal e qual.
	 *
	 * Só na lista DEV: o texto carrega o bloco da pessoa, os fatos recordados e a
	 * etapa do funil, e nada disso é para um operador de atendimento ler por
	 * acidente. Ele sempre viajou no fio; faltava um sítio que o desenhasse.
	 */
	"prompt",
	"toolDuration",
	"toolMedia",
	"timestamps",
	"actions",
	"usage",
	"model",
	"cost",
	"credits",
	"duration",
] as const;

export type AgentDisplayAxis = (typeof AGENT_DISPLAY_AXES)[number];
export type AgentDisplayMode = Readonly<Record<AgentDisplayAxis, boolean>>;

export interface AgentDisplayPolicy {
	readonly client: AgentDisplayMode;
	readonly dev: AgentDisplayMode;
	readonly allowPreviewAsClient: boolean;
}

/** Uma lista de eixos ligados vira o registo completo — todo eixo presente. */
function modeOf(on: readonly AgentDisplayAxis[]): AgentDisplayMode {
	const mode = {} as Record<AgentDisplayAxis, boolean>;
	for (const axis of AGENT_DISPLAY_AXES) mode[axis] = on.includes(axis);
	return mode;
}

/**
 * O que o painel mostra ANTES de a política chegar, e contra um servidor que
 * ainda não a sirva.
 *
 * ── O EIXO QUE DEIXOU DE EXISTIR: `expanded` ──────────────────────────────
 *
 * Ele dizia "o cartão já vem ABERTO". A decisão do dono (24/08) é que nenhum
 * painel recolhível abre sozinho ao montar, e um eixo que o front não pode
 * honrar sem desobedecer é configuração morta. Saiu daqui e do servidor no mesmo
 * dia; um servidor mais antigo que ainda o mande é ignorado por `readMode`, que
 * só lê os eixos desta lista.
 *
 * A mesma decisão do dono que está escrita no default do servidor: o raciocínio,
 * a hora, a barra de ações e a lista detalhada das ferramentas são de toda a
 * gente; abrir o JSON de um passo, os tokens, o modelo e o custo não são.
 */
export const DEFAULT_CLIENT_MODE = modeOf([
	"activity",
	"reasoning",
	"tools",
	"toolDuration",
	"timestamps",
	"actions",
	"duration",
]);

export const DEFAULT_DEV_MODE = modeOf([
	"activity",
	"reasoning",
	"reasoningRaw",
	"tools",
	"toolIo",
	"toolRaw",
	"prompt",
	"toolDuration",
	"timestamps",
	"actions",
	"usage",
	"model",
	"cost",
	"credits",
	"duration",
]);

export const DEFAULT_POLICY: AgentDisplayPolicy = {
	client: DEFAULT_CLIENT_MODE,
	dev: DEFAULT_DEV_MODE,
	allowPreviewAsClient: true,
};

export interface PlatformAccess {
	readonly platformAdmin: boolean;
	readonly agentDisplay: AgentDisplayPolicy;
}

export const platformAccessKey = ["platform", "access"] as const;

/**
 * Um modo vindo do fio, completado.
 *
 * Um servidor mais antigo manda menos eixos do que esta versão conhece, e um eixo
 * ausente tem de ser FALSO e não `undefined`: `undefined` num `v-if` desenha a
 * mesma coisa que `false`, mas num `||` de duas condições não desenha — e essa é
 * a diferença que só aparece em produção.
 */
function readMode(raw: unknown, fallback: AgentDisplayMode): AgentDisplayMode {
	if (raw === null || typeof raw !== "object") return fallback;
	const wire = raw as Record<string, unknown>;
	const mode = {} as Record<AgentDisplayAxis, boolean>;
	for (const axis of AGENT_DISPLAY_AXES) mode[axis] = wire[axis] === true;
	return mode;
}

function readPolicy(raw: unknown): AgentDisplayPolicy {
	if (raw === null || typeof raw !== "object") return DEFAULT_POLICY;
	const wire = raw as Record<string, unknown>;
	return {
		client: readMode(wire.client, DEFAULT_CLIENT_MODE),
		dev: readMode(wire.dev, DEFAULT_DEV_MODE),
		allowPreviewAsClient: wire.allowPreviewAsClient !== false,
	};
}

/**
 * A pergunta, feita uma vez.
 *
 * Só uma RECUSA é resposta — a mesma regra que já estava escrita em
 * `features/platform`, e pelo mesmo incidente: engolir toda a falha em `false`
 * fazia a consulta ter SUCESSO com a resposta errada, e o `staleTime` segurava-a
 * por um minuto. Uma queda de rede não é o servidor a dizer quem a pessoa é.
 */
export async function fetchPlatformAccess(): Promise<PlatformAccess> {
	try {
		const res = await request<{ platformAdmin: boolean; agentDisplay?: unknown }>(
			"/v3/platform/access",
		);
		return { platformAdmin: res.platformAdmin, agentDisplay: readPolicy(res.agentDisplay) };
	} catch (e) {
		if (e instanceof HttpError && (e.kind === "forbidden" || e.kind === "notFound")) {
			return { platformAdmin: false, agentDisplay: DEFAULT_POLICY };
		}
		throw e;
	}
}

/** A mesma resposta, em cache, para quem desenha. */
export function usePlatformAccessDoc() {
	return useQuery({
		queryKey: platformAccessKey,
		queryFn: fetchPlatformAccess,
		staleTime: 60_000,
	});
}
